/**
 * Download utility functions for downloading files from URLs
 */

/**
 * Download a single file from a URL
 * Works with cross-origin URLs by fetching the file as a blob
 * @param url - The URL of the file to download
 * @param filename - Optional filename for the downloaded file
 */
export const downloadFile = async (url: string, filename?: string): Promise<void> => {
    try {
        // First, try to fetch the file as a blob (works for same-origin or CORS-enabled)
        const response = await fetch(url, {
            mode: 'cors',
            credentials: 'omit'
        })

        if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status}`)
        }

        const blob = await response.blob()

        // Create a blob URL
        const blobUrl = window.URL.createObjectURL(blob)

        // Extract filename from URL if not provided
        const defaultFilename = url.split('/').pop()?.split('?')[0] || 'download'
        const finalFilename = filename || defaultFilename

        // Create download link
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = finalFilename
        link.style.display = 'none'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Cleanup blob URL
        setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl)
        }, 100)
    } catch (error) {
        console.error('Blob download failed, trying alternative method:', error)
        // Fallback: use an iframe approach for PDFs
        await downloadFileViaIframe(url, filename)
    }
}

/**
 * Download file using iframe approach (works better for cross-origin PDFs)
 */
const downloadFileViaIframe = async (url: string, filename?: string): Promise<void> => {
    return new Promise((resolve) => {
        // Create a hidden iframe
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        document.body.appendChild(iframe)

        // For PDFs, we'll try using an anchor with download attribute first
        const link = document.createElement('a')
        link.href = url
        link.download = filename || url.split('/').pop()?.split('?')[0] || 'download.pdf'
        link.target = '_blank'
        link.rel = 'noopener noreferrer'

        // Set the Content-Disposition header expectation
        // This works for some servers that support it


        // Try the modified URL first (works for Cloudinary)
        const cloudinaryUrl = getCloudinaryDownloadUrl(url)

        if (cloudinaryUrl !== url) {
            // Use Cloudinary's fl_attachment parameter
            link.href = cloudinaryUrl
            link.download = filename || 'download.pdf'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } else {
            // For other URLs, try direct download or open in new tab
            link.href = url
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        }

        // Cleanup iframe
        setTimeout(() => {
            document.body.removeChild(iframe)
            resolve()
        }, 1000)
    })
}

/**
 * Convert a Cloudinary URL to a download URL using fl_attachment
 */
const getCloudinaryDownloadUrl = (url: string): string => {
    // Check if it's a Cloudinary URL
    if (url.includes('cloudinary.com') || url.includes('res.cloudinary')) {
        // Cloudinary URLs look like:
        // https://res.cloudinary.com/xxx/image/upload/v123/path/file.pdf
        // We need to add fl_attachment flag

        // Find the upload part and insert fl_attachment after it
        const uploadIndex = url.indexOf('/upload/')
        if (uploadIndex !== -1) {
            const before = url.substring(0, uploadIndex + 8) // includes '/upload/'
            const after = url.substring(uploadIndex + 8)

            // Check if there's already a version or transformation
            if (after.startsWith('v') || after.includes('/')) {
                // Already has transforms, add fl_attachment before everything
                return `${before}fl_attachment/${after}`
            }

            return `${before}fl_attachment/${after}`
        }
    }

    // Not a Cloudinary URL, return as-is
    return url
}

/**
 * Download multiple files sequentially
 * @param files - Array of objects with url and optional filename
 * @param onProgress - Optional callback for progress updates
 */
export const downloadMultipleFiles = async (
    files: Array<{ url: string; filename?: string }>,
    onProgress?: (current: number, total: number) => void
): Promise<void> => {
    for (let i = 0; i < files.length; i++) {
        const file = files[i]
        onProgress?.(i + 1, files.length)

        await downloadFile(file.url, file.filename)

        // Small delay between downloads to prevent browser issues
        if (i < files.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500))
        }
    }
}

/**
 * Download an image file
 * @param imageUrl - The URL of the image to download
 * @param title - Title to use as filename
 */
export const downloadImage = async (imageUrl: string, title?: string): Promise<void> => {
    // Determine file extension from URL or default to jpg
    const urlExtension = imageUrl.split('.').pop()?.split('?')[0]?.toLowerCase()
    const extension = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(urlExtension || '')
        ? urlExtension
        : 'jpg'

    const filename = title
        ? `${title.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').toLowerCase()}.${extension}`
        : `image_${Date.now()}.${extension}`

    await downloadFile(imageUrl, filename)
}

/**
 * Download a PDF file
 * @param pdfUrl - The URL of the PDF to download
 * @param title - Title to use as filename
 */
export const downloadPdf = async (pdfUrl: string, title?: string): Promise<void> => {
    const filename = title
        ? `${title.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_').toLowerCase()}.pdf`
        : `document_${Date.now()}.pdf`

    // Check if it's a Cloudinary URL and convert it
    const downloadUrl = getCloudinaryDownloadUrl(pdfUrl)

    // If it's a Cloudinary URL, use the modified URL directly
    if (downloadUrl !== pdfUrl) {
        try {
            const response = await fetch(downloadUrl, {
                mode: 'cors',
                credentials: 'omit'
            })

            if (response.ok) {
                const blob = await response.blob()
                const blobUrl = window.URL.createObjectURL(blob)

                const link = document.createElement('a')
                link.href = blobUrl
                link.download = filename
                link.style.display = 'none'

                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100)
                return
            }
        } catch (error) {
            console.error('Cloudinary download failed:', error)
        }
    }

    // For non-Cloudinary URLs or if Cloudinary fails, try direct anchor approach
    const link = document.createElement('a')
    link.href = pdfUrl
    link.download = filename
    link.target = '_blank'
    link.rel = 'noopener noreferrer'

    // Try to trigger download
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // If the above doesn't trigger a download (CORS issues), show a message
    // The file will open in a new tab, which is the best we can do for cross-origin PDFs
}

/**
 * Force download by fetching and creating blob (for APIs that return file data)
 */
export const forceDownload = (data: Blob | ArrayBuffer, filename: string, mimeType?: string): void => {
    const blob = data instanceof Blob ? data : new Blob([data], { type: mimeType || 'application/octet-stream' })
    const blobUrl = window.URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    link.style.display = 'none'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100)
}

// Handle download from URL
export const getFileType = (url: string): string => {
    const extMatch = url.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/);
    const ext = extMatch ? extMatch[1] : '';
    return ext || 'FILE';
};

const getFileNameFromUrl = (url: string): string => {
    const nameMatch = url.match(/\/([^/?#]+)(?:\?|#|$)/);
    return nameMatch ? decodeURIComponent(nameMatch[1]) : 'document';
};



export const handleDownloadFromUrl = async ({ url, fileName }: { url: string; fileName?: string }) => {
    if (!url) return;

    const formattedFileName = `${fileName || getFileNameFromUrl(url)}.${getFileType(url).toLowerCase()}`;
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Download failed');
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = objectUrl;
        link.download = formattedFileName;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
    } catch (error) {
        console.error('Download error:', error);
        // Fallback: open in new tab if fetch/blob fails (e.g. CORS restriction)
        window.open(url, '_blank', 'noopener,noreferrer');
    } finally {
        // Any cleanup if necessary
    }
};

export default handleDownloadFromUrl;
